import { useCallback, useEffect, useState } from 'react';
import { useToast } from '@panwds/react-ui';
import { requestOTP, verifyOTP } from '../../../services/api/otp';
import { getSubscriptions } from '../../../services/api/subscriptions';
import {
    getSessionToken,
    isSessionValid as checkSessionValid,
    setSession,
    clearSession,
    SESSION_CHANGED_EVENT,
    notifySessionChange,
} from '../utils/session';

const OTP_DURATION_SECONDS = 300;
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LfOrXcsAAAAACZkAQIQpydGARX5WUCeUq4nefE9';
const IS_LOCALHOST = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';


/**
 * Encapsulates the OTP / reCAPTCHA / email sign-in flow that is shared across
 * Header, Incidents, and Maintenances components.
 *
 * @param {Object} options
 * @param {Function} options.onSubscribeSuccess - called after OTP verification succeeds,
 *   receives { subscriptionsData, sessionToken } so the caller can open the subscription
 *   manager, etc.
 * @returns subscription flow state + handlers
 */
export function useSubscriptionFlow({ onSubscribeSuccess } = {}) {
    const { toast, clear } = useToast();

    // --- Email / OTP state ---
    const [email, setEmail] = useState(localStorage.getItem('email') || '');
    const [otp, setOTP] = useState('');
    const [otpToken, setOTPToken] = useState(null);

    // --- Session validity ---
    const [hasValidSession, setHasValidSession] = useState(() => checkSessionValid());

    useEffect(() => {
        const onSessionChanged = () => setHasValidSession(checkSessionValid());
        window.addEventListener(SESSION_CHANGED_EVENT, onSessionChanged);
        return () => window.removeEventListener(SESSION_CHANGED_EVENT, onSessionChanged);
    }, []);

    // --- UI visibility flags ---
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const [showOTPInput, setShowOTPInput] = useState(false);
    const [showRecaptcha, setShowRecaptcha] = useState(false);
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    // Drives ManageSubscriptionsModal directly -- there is no intermediate read-only
    // review screen; once authenticated (fresh OTP or an already-valid session), the
    // user goes straight into editing their subscriptions.
    const [isManageModalOpen, setIsManageModalOpen] = useState(false);
    const [recaptchaVerified, setRecaptchaVerified] = useState(false);

    // --- Countdown timer ---
    const [countdown, setCountdown] = useState(0);
    const [startCountdown, setStartCountdown] = useState(false);

    useEffect(() => {
        let timer;
        if (startCountdown && countdown > 0) {
            timer = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        setStartCountdown(false);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [startCountdown, countdown]);

    const formatCountdown = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // --- Handlers ---

    const handleSendOTP = useCallback(async (targetEmail) => {
        const emailToUse = targetEmail || email;
        try {
            const payload = { email: emailToUse };
            const data = await requestOTP(
                { ...payload },
            );
            setOTPToken(data?.v_token);
            toast(`We have sent OTP to your email - ${emailToUse}`, { appearance: 'success' });
            setCountdown(OTP_DURATION_SECONDS);
            setStartCountdown(true);

            setTimeout(() => {
                clear();
                setShowOTPInput(true);
            }, 1000);

            return data;
        } catch (error) {
            console.error('Error requesting OTP:', error);
            toast('Failed to send OTP. Please try again.', { appearance: 'error' });
            setTimeout(() => clear(), 5000);
        }
    }, [email, toast, clear]);

    const handleVerifyOTP = useCallback(async (targetEmail, otpCode) => {
        const emailToUse = targetEmail || email;
        const otpToUse = otpCode || otp;

        try {
            const payload = {
                email: emailToUse,
                otp_code: otpToUse,
                v_token: otpToken,
            };
            const data = await verifyOTP(
                { ...payload },
            );

            if (data.success) {
                clear();
                toast('OTP is successfully verified!', { appearance: 'success' });
                setIsEmailModalOpen(false);
                setSession(data.session_token, data.expires_at);
                setHasValidSession(true);
                notifySessionChange();
                setShowRecaptcha(false);
                if (window.grecaptcha) window.grecaptcha.reset();

                try {
                    const subscriptions = await getSubscriptions(
                        `/${localStorage.getItem('email')}?session_token=${getSessionToken()}`,
                    );

                    if (subscriptions.status === 'success') {
                        setIsManageModalOpen(true);
                        setShowRecaptcha(false);
                        onSubscribeSuccess?.({ subscriptionsData: subscriptions.data, sessionToken: data.session_token });
                    } else {
                        clear();
                        toast(subscriptions.message, { appearance: subscriptions.status });
                        setShowRecaptcha(false);
                        setIsManageModalOpen(false);
                    }
                } catch (subError) {
                    if (subError?.response?.status === 404) {
                        setIsManageModalOpen(true);
                        setShowRecaptcha(false);
                        onSubscribeSuccess?.({ subscriptionsData: {}, sessionToken: data.session_token });
                    } else {
                        clear();
                        toast('Failed to load subscriptions.', { appearance: 'error' });
                        setShowRecaptcha(false);
                        setIsManageModalOpen(false);
                    }
                }
            } else {
                clear();
                setShowOTPInput(true);
                toast(data.detail, { appearance: 'error' });
            }

            setTimeout(() => clear(), 5000);
            return data;
        } catch (error) {
            clear();
            console.error('Error verifying OTP:', error);
            toast('OTP verification failed. Please try again.', { appearance: 'error' });
            setTimeout(() => clear(), 5000);
        } finally {
            setShowRecaptcha(false);
        }
    }, [email, otp, otpToken, toast, clear, onSubscribeSuccess]);

    const handleRecaptchaSuccess = useCallback(async (token) => {
        if (token) {
            setRecaptchaVerified(true);
            setShowRecaptcha(false);
            await handleSendOTP(email);
            setTimeout(() => clear(), 5000);
        }
    }, [email, handleSendOTP, clear]);

    // Show/hide reCAPTCHA container when showRecaptcha changes.
    // The widget itself is rendered by index.html (data-sitekey on the container div).
    // We bridge into the global callback via window._onRecaptchaSuccess.
    useEffect(() => {
        const container = document.getElementById('g-recaptcha-container');

        if (showRecaptcha) {
            window._onRecaptchaSuccess = handleRecaptchaSuccess;

            setTimeout(() => {
                if (container) {
                    container.style.display = 'block';
                    container.style.position = 'fixed';
                    container.style.top = '50%';
                    container.style.left = '50%';
                    container.style.transform = 'translate(-50%, -50%)';
                    container.style.zIndex = 1000000000;
                }
            }, 100);
        }

        return () => {
            if (container) {
                container.style.display = 'none';
            }
        };
    }, [showRecaptcha, handleRecaptchaSuccess]);

    const resetSession = useCallback(() => {
        clearSession();
        setHasValidSession(false);
        notifySessionChange();
        setShowOTPInput(false);
        setShowRecaptcha(false);
        setIsEmailModalOpen(true);
    }, []);

    const signOut = useCallback(() => {
        localStorage.removeItem('email');
        setEmail('');
        clearSession();
        setHasValidSession(false);
        notifySessionChange();
        setShowRecaptcha(false);
        if (window.grecaptcha) window.grecaptcha.reset();
        setIsManageModalOpen(false);
    }, []);

    const EMAIL_REGEX = /^(?!\.)(?!.*\.\.)([A-Z0-9_'+\-\.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9\-]*\.)+[A-Z]{2,}$/i;

    const handleEmailSubmit = useCallback(async () => {
        if (EMAIL_REGEX.test(email)) {
            clear();
            if (!showOTPInput) {
                if (IS_LOCALHOST) {
                    handleSendOTP(email);
                } else {
                    setShowRecaptcha(true);
                }
            } else {
                if (checkSessionValid()) {
                    setShowRecaptcha(false);
                    setIsManageModalOpen(true);
                } else {
                    await handleVerifyOTP(email, otp);
                }
            }
        } else {
            toast('Please enter valid Email Address', { appearance: 'error' });
            setTimeout(() => clear(), 5000);
        }
    }, [email, otp, showOTPInput, toast, clear, handleVerifyOTP, handleSendOTP]);

    const closeEmailModal = useCallback(() => {
        setShowOTPInput(false);
        setOTP('');
        setShowRecaptcha(false);
        setIsEmailModalOpen(false);
        if (window.grecaptcha) window.grecaptcha.reset();
    }, []);

    return {
        // State
        email,
        setEmail,
        otp,
        setOTP,
        otpToken,
        hasValidSession,
        isEmailModalOpen,
        setIsEmailModalOpen,
        showOTPInput,
        setShowOTPInput,
        showRecaptcha,
        setShowRecaptcha,
        showSubscriptionModal,
        setShowSubscriptionModal,
        isManageModalOpen,
        setIsManageModalOpen,
        recaptchaVerified,
        countdown,
        startCountdown,
        formatCountdown,

        // Handlers
        handleSendOTP,
        handleVerifyOTP,
        handleRecaptchaSuccess,
        handleEmailSubmit,
        closeEmailModal,
        resetSession,
        signOut,

        // Utilities
        EMAIL_REGEX,
    };
}

export default useSubscriptionFlow;
